import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as PR from "../../prime-modules/index";
import AdminFooter from "../layout/admin-footer";
import AdminHeader from "../layout/admin-header";
import TabsBlock from "./TabsBlock";
import GraphsBlock from "./GraphsBlock";
import "../dashboard/Dashboard.scss"; // CSS
import { getTabs, getGraphs, getMonthGraphs, getActiveStats, getCancelledStats, getActiveGraph, getRecurringTab } from "../../services/api";
import moment from "moment";
import { startOfMonth, endOfMonth, subMonths } from 'date-fns';

const getFormattedDateRange = (dates, selectedOption) => {
  if (!dates?.[0]) {
    return null;
  }

  const formattedStart = moment(dates[0]).format("DD-MM-YYYY");
  const formattedEnd = (dates[1] && selectedOption === "Current month")
    ? moment().format("DD-MM-YYYY")
    : dates[1]
      ? moment(dates[1]).format("DD-MM-YYYY")
      : formattedStart;

  return {
    formattedStart,
    formattedEnd,
  };
};

const getDatesForOption = (type) => {
  const today = new Date();
  switch (type) {
    case "Current month":
      return [startOfMonth(today), endOfMonth(today)];
    case "Last month": {
      const lastMonth = subMonths(today, 1);
      return [startOfMonth(lastMonth), endOfMonth(lastMonth)];
    }
    case "Last 3 month":
      return [subMonths(today, 3), today];
    case "Custom":
      return [];
    default:
      return [];
  }
};

const Dashboard = () => {
  const appName = process.env.REACT_APP_NAME;
  const dispatch = useDispatch();
  const adminData = useSelector((state) => state.adminAuth.adminSessionData);
  const headers = useMemo(() => {
    return { sessionid: adminData.sessionId };
  }, [adminData.sessionId]);
  const navigate = useNavigate();
  const [selectedTabs, setSelectedTabs] = useState([]);
  const [recurringTab, setRecurringTab] = useState([]);
  const [activeTab, setActiveTab] = useState([]);
  const [cancelTab, setCancelTab] = useState([]);
  const [selectedGraphs, setSelectedGraphs] = useState([]);
  const [selectedOrderGraphs, setSelectedOrderGraphs] = useState([]);
  const [selectedRevenueGraphs, setSelectedRevenueGraphs] = useState([]);
  const [selectedMonthGraph, setSelectedMonthGraph] = useState([]);
  const [activeSubscriptions, setActiveSubscriptions] = useState([])
  const [selectedOption, setSelectedOption] = useState('Current month');
  const [selectedDisplayOption, setSelectedDisplayOption] = useState('Current month');
  const [previousInterval, setPreviousInterval] = useState("")
  const [isLoading, setIsLoading] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);

  //loader
  const [loadingGraphs, setLoadingGraphs] = useState({
    bundlesSold: false,
    orderRevenueGraph: false,
    popularBundles: false,
    activeGraph: false,
    stats: false
  });

  const [dates, setDates] = useState(() => getDatesForOption("Current month"));
  const [appliedDates, setAppliedDates] = useState(() => getDatesForOption("Current month"));
  const [displayDate, setDisplayDate] = useState({startDate: dates[0] ? moment(dates[0]).format("DD-MM-YYYY") : "", endDate: dates[1] ? moment().format("DD-MM-YYYY") : "" })

  const predefinedOptions = [
    { label: "Current month", value: "Current month" },
    { label: "Last month", value: "Last month" },
    { label: "Last 3 month", value: "Last 3 month" },
    { label: "Custom", value: "Custom" }
  ];


  const [selectedGraphValues, setSelectedGraphValues] = useState(() => {
    const defaultValues = [
      "bundlesSold",
      "popularBundles",
      "ordersRevenuePerDay"
    ];
    if (appName === 'data2go-iot') {
      return [...defaultValues, "activeSubscription"];
    }

    return defaultValues;
  });

  const graphOptions = [
    { label: "Country wise bundle sold", value: "bundlesSold" },
    { label: "Popular bundles", value: "popularBundles" },
    ...(appName === 'data2go-iot'
      ? [{ label: "Active subscription", value: "activeSubscription" }]
      : []),
    { label: "Orders & Revenue per day", value: "ordersRevenuePerDay" },
  ];

  const items = [
    {
      label: "Dashboard",
      command: () => {
        navigate("/dashboard");
      },
    },
  ];

  const home = { icon: "pi pi-home", url: "/" };

  const displayTabs = useCallback((startDate, endDate) => {
    setLoadingGraphs(prev => ({ ...prev, stats: true }));
    return new Promise((resolve) => {
    const ids = "revenue,bundlesSold";
    getTabs(dispatch, headers, ids, startDate, endDate, (response) => {
      if (response.result === "SUCCESS") {
        setSelectedTabs(response?.data?.statistics);
        setPreviousInterval(response?.data?.dayCount)
      }
      setLoadingGraphs(prev => ({ ...prev, stats: false }));
      resolve();
    })
    });
  }, [dispatch, headers]);

  const displayRecurringTab = useCallback((startDate, endDate) => {
    return new Promise((resolve) => {
    getRecurringTab(dispatch, headers, startDate, endDate, (response) => {
      if (response.result === "SUCCESS") {
        setRecurringTab(response?.data?.statistics);
      }
      resolve();
    })
    });
  }, [dispatch, headers]);

  const displayActiveTab = useCallback((startDate, endDate) => {
    return new Promise((resolve) => {
    getActiveStats(dispatch, headers, startDate, endDate, (response) => {
      if (response.result === "SUCCESS") {
        setActiveTab(response?.data?.statistics);
      }
      resolve()
    })
    });
  }, [dispatch, headers]);

  const displayCancelledTab = useCallback((startDate, endDate) => {
    return new Promise((resolve) => {
    getCancelledStats(dispatch, headers, startDate, endDate, (response) => {
      if (response.result === "SUCCESS") {
        setCancelTab(response?.data?.statistics);
      }
      resolve()
    })
    });
  }, [dispatch, headers]);

  const displayGraphs = useCallback((startDate, endDate) => {
    setLoadingGraphs(prev => ({ ...prev, bundlesSold: true }));
    return new Promise((resolve) => {
    const ids = "bundlesSold";
    getGraphs(dispatch, headers, ids, startDate, endDate, (response) => {
      if (response.result === "SUCCESS") {
        setSelectedGraphs(response?.data);
      }
      setLoadingGraphs(prev => ({ ...prev, bundlesSold: false }));
      resolve()
    })
    });
  }, [dispatch, headers]);

  const displayOrdersGraphs = useCallback((startDate, endDate) => {
    setLoadingGraphs(prev => ({ ...prev, orderRevenueGraph: true }));
    return new Promise((resolve) => {
    const ids = "ordersPerDay";
    getGraphs(dispatch, headers, ids, startDate, endDate, (response) => {
      if (response.result === "SUCCESS") {
        setSelectedOrderGraphs(response?.data);
      }
      setLoadingGraphs(prev => ({ ...prev, orderRevenueGraph: false }));
      resolve()
    })
    });
  }, [dispatch, headers]);

  const displayRevenueGraphs = useCallback((startDate, endDate) => {
    return new Promise((resolve) => {
    const ids = "revenuePerDay";
    getGraphs(dispatch, headers, ids, startDate, endDate, (response) => {
      if (response.result === "SUCCESS") {
        setSelectedRevenueGraphs(response?.data);
      }
      resolve()
    })
    });
  }, [dispatch, headers]);

  const displayTopBundles = useCallback((startDate, endDate) => {
    setLoadingGraphs(prev => ({ ...prev, popularBundles: true }));
    return new Promise((resolve) => {
    getMonthGraphs(dispatch, headers, startDate, endDate, (response) => {
      if (response.result === "SUCCESS") {
        setSelectedMonthGraph(response?.data);
      }
      setLoadingGraphs(prev => ({ ...prev, popularBundles: false }));
      resolve()
    })
    });
  }, [dispatch, headers]);

  const displayActiveSubscription = useCallback((startDate, endDate) => {
    setLoadingGraphs(prev => ({ ...prev, activeGraph: true }));
    return new Promise((resolve) => {
    getActiveGraph(dispatch, headers, startDate, endDate, (response) => {
      if (response.result === "SUCCESS") {
        setActiveSubscriptions(response?.data);
      }
      setLoadingGraphs(prev => ({ ...prev, activeGraph: false }));
      resolve()
    })
    });
  }, [dispatch, headers]);

  const fetchDashboardData = useCallback((startDate, endDate) => {
    const apiCalls = [
      displayTabs(startDate, endDate),
      displayGraphs(startDate, endDate),
      displayTopBundles(startDate, endDate),
      displayOrdersGraphs(startDate, endDate),
      displayRevenueGraphs(startDate, endDate),
    ];

    if (appName === "data2go-iot") {
      apiCalls.push(
        displayRecurringTab(startDate, endDate),
        displayActiveTab(startDate, endDate),
        displayCancelledTab(startDate, endDate),
        displayActiveSubscription(startDate, endDate)
      );
    }

    return Promise.all(apiCalls);
  }, [
    appName,
    displayTabs,
    displayGraphs,
    displayOrdersGraphs,
    displayRevenueGraphs,
    displayTopBundles,
    displayRecurringTab,
    displayActiveTab,
    displayCancelledTab,
    displayActiveSubscription,
  ]);

  const handleApply = useCallback(() => {
    const formattedRange = getFormattedDateRange(dates, selectedOption);

    if (formattedRange) {
      setAppliedDates(dates);
      setDisplayDate({
        startDate: formattedRange.formattedStart,
        endDate: formattedRange.formattedEnd
      });
      setSelectedDisplayOption(selectedOption);
      setShowCalendar(false);
    }
  }, [dates, selectedOption]);

  const handleOptionChange = (e) => {
    const value = e.value;
    setSelectedOption(value);
    const nextDates = getDatesForOption(value);
    setDates(nextDates);
    if(value === "Custom"){
      setShowCalendar(true)
    } else {
      const nextDates = getDatesForOption(value);
      setDates(nextDates);
      const formattedRange = getFormattedDateRange(nextDates, value);

      if (formattedRange) {
        setAppliedDates(nextDates);
        setDisplayDate({
          startDate: formattedRange.formattedStart,
          endDate: formattedRange.formattedEnd
        });
      }
      setSelectedDisplayOption(value);
      setShowCalendar(false);
    }
  };
  
  useEffect(() => {
    const formattedRange = getFormattedDateRange(appliedDates, selectedDisplayOption);

    if (adminData.sessionId && formattedRange) {
      setIsLoading(true);
      fetchDashboardData(formattedRange.formattedStart, formattedRange.formattedEnd).finally(() => {
        setIsLoading(false);
      });
    }
  }, [
    adminData.sessionId,
    appliedDates,
    fetchDashboardData,
    selectedDisplayOption,
  ]);

  return (
    <>
      <div className="main">
        <div className="layout-sidebar">
          <AdminHeader />
        </div>
        <div className="layout-content-wrapper relative">
          <section className="admin-users-section">
            <div className="grid grid-nogutter align-items-center">
              <div className="col-12">
                <div className="heading-sec flex align-items-center justify-content-between">
                  <div>
                    <div className="flex align-items-center">
                      <div className="card flex align-items-center">
                        <h1>Dashboard</h1>{" "}
                      </div>
                      <div className="breadcrumb-section">
                        <PR.BreadCrumb model={items} home={home} />
                      </div>
                    </div>
                  </div>
                  <div className="current-previous-btns-wrapper">
                      <div className="full-width flex gap-2">
                        <PR.MultiSelect
                          value={selectedGraphValues}
                          options={graphOptions}
                          onChange={(e) => setSelectedGraphValues(e.value)}
                          placeholder="Show or hide panel"                    
                          selectionLimit={4}
                          display="comma"
                          maxSelectedLabels={1}                
                          style={{ minWidth: '250px' }}
                          selectedItemTemplate={() => 'Show or hide panel'}
                          panelClassName="show-hide-panel-multiselect"
                        />
                      <div className="date-dropdown-block">
                        <PR.Dropdown
                          value={selectedOption}
                          options={predefinedOptions}
                          onChange={handleOptionChange}
                          placeholder="Select Date Range"
                        />
                        {selectedOption === "Custom" && showCalendar && (
                          <div className="custom-calendar">
                            <PR.Calendar
                              selectionMode="range"
                              value={dates}
                              maxDate={new Date()}
                              onChange={(e) => setDates(e.value)}
                              inline
                            />
                            <div className="calendar-footer">
                              <PR.Button label="OK"
                                onClick={handleApply}
                                disabled={!dates?.[0]} className="ok-button" />
                            </div>
                          </div>
                        )}
                      </div>
                      </div>
                  </div>
                </div>
              </div>
            </div>
            {isLoading ? (
              <div className="dashboard-loading">
                <PR.ProgressSpinner />
              </div>
            ) : ( 
              <>
              <div className="showing-data-p">
                <p>
                  {(selectedDisplayOption === "Custom" && !displayDate?.endDate) ? (
                    <>
                      Showing data of <strong>{displayDate?.startDate}</strong>
                    </>
                  ) : (
                    <>
                      Showing data from <strong>{displayDate?.startDate}</strong> to <strong>{displayDate?.endDate}</strong>
                    </>
                  )}
                </p>
            </div>
            <div className="dashboard_section">
              <TabsBlock
                selectedTabs={selectedTabs}
                recurringTab={recurringTab}
                activeTab={activeTab}
                cancelTab={cancelTab}
                selectedDisplayOption= {selectedDisplayOption}
                dates={dates}
                displayDate={displayDate}
                previousInterval={previousInterval}
                loadingGraphs={loadingGraphs}
              />
              <GraphsBlock
                selectedGraphs={selectedGraphs}
                selectedMonthGraph={selectedMonthGraph}
                selectedOrderGraphs={selectedOrderGraphs}
                selectedRevenueGraphs={selectedRevenueGraphs}
                activeSubscriptions={activeSubscriptions}
                selectedGraphValues={selectedGraphValues}
                loadingGraphs={loadingGraphs}
              />

            </div>
            </>
            )}
          </section>
          <AdminFooter />
        </div>
      </div>
    </>
  );
};

export default Dashboard;
